import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const sections = document.querySelectorAll("section[data-section]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.4 },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const tocItems = [
    { id: "intro", label: "1. Introduction" },
    { id: "acceptance", label: "2. Acceptance of Terms" },
    { id: "use", label: "3. Using Omnyx Services" },
    { id: "accounts", label: "4. Account Responsibilities" },
    { id: "payments", label: "5. Payments & Fees" },
    { id: "refunds", label: "6. Refunds" },
    { id: "conduct", label: "7. Acceptable Use" },
    { id: "liability", label: "8. Limitation of Liability" },
    { id: "ip", label: "9. Intellectual Property" },
    { id: "termination", label: "10. Termination" },
    { id: "governing-law", label: "11. Governing Law" },
    { id: "changes", label: "12. Changes to Terms" },
    { id: "global-contact", label: "13. Contact (Omnyx)" },

    { id: "ig-header", label: "InGroove – Platform Terms" },
    { id: "ig-nature", label: "1. What InGroove Is (and Is Not)" },
    { id: "ig-hubs", label: "2. Hub Responsibilities" },
    { id: "ig-content", label: "3. Content Standards for Hubs" },
    { id: "ig-buyers", label: "4. Ticket Buyer Responsibilities" },
    { id: "ig-fraud", label: "5. Fraud & Misconduct" },
    { id: "ig-events", label: "6. Event Issues & Disputes" },
    { id: "ig-payouts", label: "7. Hub Payouts" },
    { id: "ig-referrals", label: "8. Referral Program" },
    { id: "ig-tickets", label: "9. Ticket Rules" },
    { id: "ig-risks", label: "10. Personal Risk & Safety" },
    { id: "ig-liability", label: "11. InGroove Liability Disclaimer" },
    { id: "ig-contact", label: "Contact (InGroove)" },
  ];

  return (
    <div className="relative mx-auto flex max-w-site gap-10 px-6 py-20">
      {/* TABLE OF CONTENTS */}
      <aside className="hidden h-fit w-64 lg:sticky lg:top-24 lg:block">
        <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-site-ink">On this page</h3>
        <ul className="space-y-2 border-l border-site-hairline pl-4">
          {tocItems.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={`block text-sm transition-colors ${
                  activeSection === item.id
                    ? "font-semibold text-omnyx-teal"
                    : "text-site-muted hover:text-site-ink"
                }`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="mt-6 border-t border-site-hairline pt-4">
          <Link
            to="/privacy"
            className="font-semibold text-omnyx-teal hover:text-omnyx-teal-dark"
          >
            → Privacy Policy
          </Link>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="legal-content flex-1 space-y-10">
        <div className="border-b border-site-hairline pb-10">
          <h1 className="font-heading text-4xl font-extrabold tracking-tightest text-site-ink md:text-5xl">
            Omnyx – Terms & Conditions
          </h1>
          <p className="mt-3 text-site-muted">
            Last Updated: {new Date().getFullYear()}
          </p>
        </div>

        {/* 1 INTRO */}
        <section id="intro" data-section className="mb-10 scroll-mt-28">
          <p>
            These Terms & Conditions (&quot;Terms&quot;) govern your use of all
            Omnyx (Pty) Ltd platforms, including websites, apps, and systems
            developed under Omnyx. By using our services, you agree to these
            Terms.
          </p>
        </section>

        {/* 2 ACCEPTANCE */}
        <section id="acceptance" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            2. Acceptance of Terms
          </h2>
          <p>
            By creating an account or accessing any Omnyx platform, you confirm
            that you:
          </p>
          <ul className="list-disc ml-6 space-y-2 mt-2">
            <li>
              are at least 16 years old (or have legal consent if required)
            </li>
            <li>agree to these Terms in full</li>
            <li>agree to our Privacy Policy</li>
          </ul>
        </section>

        {/* 3 USAGE */}
        <section id="use" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            3. Using Omnyx Services
          </h2>
          <p>You agree not to misuse our platforms. This includes:</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>No hacking or security bypass attempts</li>
            <li>No impersonation or fraudulent activity</li>
            <li>No posting harmful, explicit or illegal content</li>
            <li>No scraping or unauthorized automation</li>
          </ul>
        </section>

        {/* 4 ACCOUNTS */}
        <section id="accounts" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            4. Account Responsibilities
          </h2>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>You are responsible for keeping your password secure.</li>
            <li>You must provide accurate information.</li>
            <li>You may not share or sell accounts.</li>
          </ul>
        </section>

        {/* PAYMENTS */}
        <section id="payments" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">5. Payments & Fees</h2>
          <p>
            Payments made on Omnyx products (including InGroove) are processed
            through secure third-party gateways such as PayFast or Ozow. Omnyx
            does not store card details.
          </p>
        </section>

        {/* REFUNDS */}
        <section id="refunds" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">6. Refunds</h2>
          <p>Refunds depend on the product or service used.</p>
          <p className="mt-2">For InGroove specifically:</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>Refunds for cancelled events are issued in Groovies only</li>
            <li>No cash refunds unless required by law</li>
            <li>Groovies cannot be converted to cash</li>
          </ul>
        </section>

        {/* CONDUCT */}
        <section id="conduct" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">7. Acceptable Use</h2>

          <p>
            All activity on Omnyx platforms must be lawful, respectful, and
            suitable for a general audience.
          </p>

          <p className="mt-3 font-semibold text-red-600">Prohibited Content:</p>

          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>Explicit, pornographic, or sexually graphic content</li>
            <li>Sexualized nudity or fetish content</li>
            <li>Violent, graphic, or gory material</li>
            <li>Hate speech, harassment, or threats</li>
            <li>Content that promotes illegal activity</li>
            <li>
              Content involving minors in any sexual or exploitative context
            </li>
          </ul>
          {/* 
          <p className="mt-3">
            Any content that violates this section may be removed without
            notice, and the responsible account may be suspended or permanently
            terminated.
          </p> */}
          <p className="mt-3">
            Omnyx reserves the right to remove or disable access to any content,
            listing, or account that violates these Terms, at our sole
            discretion and without prior notice.
          </p>
        </section>

        {/* LIABILITY */}
        <section id="liability" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            8. Limitation of Liability
          </h2>
          <p>Omnyx is not responsible for:</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>losses caused by user error</li>
            <li>third-party services or outages</li>
            <li>damages arising from inaccurate user-submitted content</li>
            <li>
              service interruptions, maintenance, or platform unavailability
            </li>
          </ul>
          <p className="mt-3">
            Omnyx shall not be liable for failure or delay in performance
            resulting from events beyond reasonable control, including natural
            disasters, government actions, power outages, or network failures.
          </p>
        </section>

        {/* IP */}
        <section id="ip" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            9. Intellectual Property
          </h2>
          <p>
            All Omnyx platforms, branding, code, and assets are owned by Omnyx
            (Pty) Ltd unless stated otherwise.
          </p>
        </section>

        {/* TERMINATION */}
        <section id="termination" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">10. Termination</h2>
          <p>We may suspend or terminate accounts for:</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>fraud</li>
            <li>abuse</li>
            <li>breach of these Terms</li>
            <li>posting explicit, violent, or prohibited content</li>
          </ul>
        </section>

        <section id="governing-law" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">11. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the Republic of South
            Africa. Any disputes shall be subject to the exclusive jurisdiction
            of South African courts.
          </p>
        </section>

        {/* CHANGES */}
        <section id="changes" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">
            12. Changes to These Terms
          </h2>
          <p>
            We may update these Terms from time to time. Continued use of our
            services means you accept the updated version.
          </p>
        </section>

        {/* CONTACT GLOBAL */}
        <section
          id="global-contact"
          data-section
          className="mb-20 scroll-mt-28"
        >
          <h2 className="text-2xl font-semibold mb-3">13. Contact (Omnyx)</h2>
          <p>Email: legal@omnyx.co.za</p>
        </section>

        {/* INGROOVE SECTION HEADER */}
        <section id="ig-header" data-section className="mb-10 scroll-mt-28">
          <h2 className="mb-6 font-heading text-3xl font-extrabold tracking-tightest text-omnyx-teal">
            InGroove – Platform Terms & Conditions
          </h2>
          <p className="opacity-90">
            These terms apply specifically to users, hubs, organizers, and
            buyers using the InGroove ticketing platform.
          </p>
        </section>

        {/* WHAT INGROOVE IS */}
        <section id="ig-nature" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            1. What InGroove Is (and Is Not)
          </h3>
          <p>
            InGroove is strictly a **point-of-sale platform** for hubs (event
            organizers) to advertise events and sell tickets.
          </p>

          <p className="mt-3 font-semibold">InGroove does NOT:</p>
          <ul className="list-disc ml-6 mt-1 space-y-2">
            <li>host or operate events</li>
            <li>vet hubs beyond basic verification</li>
            <li>guarantee the accuracy of hub content</li>
            <li>control event safety or logistics</li>
          </ul>

          <p className="mt-2 font-semibold text-red-600">
            InGroove is not liable for any loss, harm, injury, fraud, or damages
            caused by hubs or events.
          </p>
        </section>

        {/* HUBS */}
        <section id="ig-hubs" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            2. Hub (Organizer) Responsibilities
          </h3>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>Provide accurate event information</li>
            <li>Deliver the event as advertised</li>
            <li>Manage refunds for cancellations</li>
            <li>Ensure safety and legal compliance</li>
            <li>Take responsibility for operators they add</li>
          </ul>
        </section>

        <section id="ig-content" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            3. Content Standards for Hubs
          </h3>

          <p>
            Hubs are strictly prohibited from posting, promoting, or selling
            tickets for events that include:
          </p>

          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>Explicit sexual content or performances</li>
            <li>Graphic violence or violent imagery</li>
            <li>Events primarily intended to sexually arouse attendees</li>
            <li>
              Any content that would be classified as adult-only or pornographic
            </li>
          </ul>

          <p className="mt-3 font-semibold text-red-600">
            Violation of this policy will result in immediate suspension or
            termination of the hub account, without payout if legally permitted.
          </p>
        </section>

        {/* BUYERS */}
        <section id="ig-buyers" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            4. Ticket Buyer Responsibilities
          </h3>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>Check event details before buying</li>
            <li>Arrive with correct ticket/QR code</li>
            <li>Follow event rules and venue policies</li>
          </ul>
        </section>

        {/* FRAUD */}
        <section id="ig-fraud" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">5. Fraud & Misconduct</h3>
          <p>
            Fraudulent hubs will be suspended. Investigations may involve law
            enforcement.
          </p>
          <p className="mt-2 font-semibold">
            InGroove is not liable for losses caused by fraudulent hubs.
          </p>
        </section>

        {/* EVENT ISSUES */}
        <section id="ig-events" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            6. Event Issues & Disputes
          </h3>
          <p>Any disputes regarding the event must be resolved between:</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>The Hub (organizer)</li>
            <li>The Buyer (ticket holder)</li>
          </ul>
          <p className="mt-2">
            InGroove cannot mediate or enforce outcomes. We only facilitate
            ticket transactions.
          </p>
        </section>

        {/* PAYOUTS */}
        <section id="ig-payouts" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">7. Payouts</h3>
          <p>Hubs may request payouts after events end.</p>
        </section>

        {/* REFERRAL PROGRAM */}
        <section id="ig-referrals" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">8. Referral Program</h3>
 
          <p>
            InGroove offers an optional Referral Program allowing eligible
            users to earn a commission by referring new hubs to the platform.
            Participation in the Referral Program is subject to the terms
            below, in addition to all other provisions of these Terms.
          </p>
 
          <p className="mt-3 font-semibold">8.1 Eligibility</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>
              Any registered InGroove user may become a referrer. A hub
              account is not required to participate.
            </li>
            <li>
              To become a referrer, a user must register as a referrer via
              the InGroove web platform and obtain a unique referral
              link/code.
            </li>
          </ul>
 
          <p className="mt-3 font-semibold">8.2 Linking a Referral</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>
              A referral is created when a new hub registers using a
              referrer&apos;s unique link or code. Each hub may only be linked
              to one referrer — the referral associated with that hub&apos;s
              registration is final, and no other user may claim credit for
              the same hub.
            </li>
            <li>Only new hubs qualify. Existing hubs cannot be referred.</li>
          </ul>
 
          <p className="mt-3 font-semibold">8.3 Qualifying Window</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>
              The referred hub must create their first paid event within 6
              (six) months of registering via the referral link. The event
              itself does not need to take place within this 6-month period —
              only the creation of the event is required within the window.
            </li>
            <li>
              If the referred hub does not create a paid event within this
              window, the referral is automatically voided and no commission
              will be payable in respect of that referral.
            </li>
          </ul>
 
          <p className="mt-3 font-semibold">8.4 Commission</p>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>
              InGroove charges a commission on valid ticket sales, at a rate
              set by InGroove from time to time (see Section 8.10). Where a
              qualifying referral applies, the referrer earns one-third
              (1/3) of InGroove&apos;s commission on the referred hub&apos;s
              qualifying first event, with InGroove retaining the remaining
              two-thirds (2/3).
            </li>
            <li>
              This split is proportional, not a fixed percentage point. If
              InGroove&apos;s commission rate changes, the referrer&apos;s
              share changes with it automatically, always remaining
              one-third of whatever commission InGroove earns on the
              qualifying event. The referral share is paid out of
              InGroove&apos;s own commission and does not increase the total
              amount charged to the hub.
            </li>
            <li>
              Commission applies only to valid, completed ticket sales.
              Refunded, cancelled, or postponed tickets/events do not count
              toward the referral commission.
            </li>
            <li>
              Referral commission is only calculated and becomes payable once
              InGroove has itself received and retained its own commission on
              the relevant payout to the hub. If InGroove does not earn a
              commission on a transaction, no referral commission is due.
            </li>
          </ul>
 
          <p className="mt-3 font-semibold">8.5 Payout</p>
          <p className="mt-2">
            Referral commission is paid out in the same payout cycle as the
            corresponding hub payout, at InGroove&apos;s discretion, following
            deduction of InGroove&apos;s own commission.
          </p>
 
          <p className="mt-3 font-semibold">8.6 Scope of the Relationship</p>
          <p className="mt-2">
            The referral relationship applies only to the referred hub&apos;s
            first qualifying paid event. No further commission accrues on
            subsequent events created by that hub, unless InGroove elects, at
            its sole discretion, to extend or continue the relationship based
            on business needs.
          </p>
 
          <p className="mt-3 font-semibold text-red-600">
            8.7 Fraud & Self-Referral
          </p>
          <p className="mt-2">
            Referrers may not refer themselves, or create, control, or
            facilitate the creation of fake or fraudulent hubs for the
            purpose of earning referral commission. Any referral found to
            involve self-referral, fraud, or fake hub accounts will result in
            all associated amounts being voided, and may result in
            suspension or termination of the referrer&apos;s account.
          </p>
 
          <p className="mt-3 font-semibold">8.8 Hub Violations</p>
          <p className="mt-2">
            If a referred hub is found to be in violation of these Terms
            (including fraud, misconduct, or prohibited content), that hub
            and its events will be voided in accordance with Section 5
            (Fraud & Misconduct) and Section 3 (Content Standards for Hubs).
            As InGroove earns no commission in such cases, no referral
            commission is payable in respect of that hub.
          </p>
 
          <p className="mt-3 font-semibold">8.9 Referrer Account Standing</p>
          <p className="mt-2">
            If a referrer&apos;s account is suspended or under investigation,
            any pending referral payout may be held or voided at InGroove&apos;s
            sole discretion, depending on the nature and severity of the
            issue. Where the suspension or investigation is unrelated to the
            referral itself, InGroove will make reasonable efforts to pay out
            amounts already earned in good standing, but this remains subject
            to InGroove&apos;s discretion.
          </p>
 
          <p className="mt-3 font-semibold">
            8.10 Changes to or Termination of the Program
          </p>
          <p className="mt-2">
            InGroove may change referral commission rates, or suspend or end
            the Referral Program entirely, at any time and at its sole
            discretion. InGroove is not obligated to notify existing
            referrers of such changes, though it may do so as a courtesy.
            Any referral commission already earned and pending at the time of
            such changes will still be paid out.
          </p>
        </section>

        {/* TICKETS */}
        <section id="ig-tickets" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">9. Ticket Rules</h3>
          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>Tickets are non-transferable unless otherwise stated</li>
            <li>QR codes must remain unaltered</li>
            <li>Lost tickets cannot always be recovered</li>
          </ul>
        </section>

        {/* SAFETY */}
        <section id="ig-risks" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            10. Personal Risk & Safety
          </h3>
          <p>
            Attending an event involves personal risk. InGroove is not
            responsible for injuries, incidents, or losses.
          </p>
        </section>

        {/* LIABILITY */}
        <section id="ig-liability" data-section className="mb-10 scroll-mt-28">
          <h3 className="text-xl font-semibold mb-3">
            11. InGroove Liability Disclaimer
          </h3>

          <p className="font-semibold text-red-600">
            InGroove is NOT liable for:
          </p>

          <ul className="list-disc ml-6 mt-2 space-y-2">
            <li>event cancellations</li>
            <li>quality of the event</li>
            <li>misconduct by hubs or operators</li>
            <li>fraudulent event organizers</li>
            <li>injuries, damages, or losses at events</li>
          </ul>

          <p className="mt-2">
            We provide a platform — the event experience is the hub’s
            responsibility.
          </p>
        </section>

        {/* CONTACT */}
        <section id="ig-contact" data-section className="mb-10 scroll-mt-28">
          <h2 className="text-2xl font-semibold mb-3">Contact (InGroove)</h2>
          <p>Email: ceo@omnyx.co.za</p>

          <Link
            to="/privacy"
            className="inline-block mt-6 font-semibold text-omnyx-teal hover:text-omnyx-teal-dark"
          >
            → Privacy Policy
          </Link>
        </section>
      </main>
    </div>
  );
}
